import { compact } from 'lodash'
import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Criteria } from './Criteria'
import { Feature } from './Feature'
import { Option } from './Option'
import { PSComponent } from './PSComponent'
import { ReportCriteria } from './ReportCriteria'
import { FKType } from './SQLBuilder'
import { User } from './User'

type ReportConstructorArgs = Omit<
    ConstructorArgs<() => Report>,
    'dimensions' | 'criterias' | 'measures'
> & {
    dimensions: PSComponent[] | string[] | null
    criterias: Criteria[] | string[] | null
    measures: PSComponent[] | string[] | null
}

export class Report extends BaseRecord {
    readonly recordType = 'report'

    title?: string
    date?: moment.Moment
    endDate?: moment.Moment
    dateType?: string
    dateCompareType?: string
    measures: PSComponent[] = []
    dimensions: PSComponent[] = []
    criterias: ReportCriteria[] = []
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<ReportConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.date = this.propertyFromDateArg(data.date)
        this.endDate = this.propertyFromDateArg(data.end_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.title = data.title ?? undefined
        this.dateType = data.date_type ?? undefined
        this.dateCompareType = data.date_compare_type ?? undefined

        const owner = PSApplication.me()
        this.dimensions = compact(
            (data.dimensions ?? []).map((data) =>
                data instanceof PSComponent
                    ? data
                    : CacheFactory.getInstance().getRecord(PSComponent, data)
            )
        ).filter((component) => Report.allowComponent(component, owner))
        this.criterias = compact(
            (data.criterias ?? []).map((data) =>
                data instanceof Criteria
                    ? data
                    : CacheFactory.getInstance().getRecord(Criteria, data)
            )
        ).filter((criteria) => Report.allowCriteria(criteria, owner))
        this.measures = compact(
            (data.measures ?? []).map((data) =>
                data instanceof PSComponent
                    ? data
                    : CacheFactory.getInstance().getRecord(PSComponent, data)
            )
        ).filter((component) => Report.allowComponent(component, owner))
    }

    clone(overrides?: Partial<ReportConstructorArgs>): Report {
        return new Report({ ...this.serializeForClone(), ...overrides })
    }

    get isCustom(): boolean {
        return this.id === undefined || !this.id.startsWith('report_')
    }

    get dateDisplayString(): string {
        if (this.dateType === 'custom') return this.customDateString()
        else {
            for (const opt of Report.dateRangeStrings()) {
                if (opt.id === this.dateType) return opt.label!
            }
        }
        return ''
    }

    private customDateString(): string {
        const isSameYear = this.date?.isSame(this.endDate, 'year') ?? false
        const isSameMonthAndYear = isSameYear && (this.date?.isSame(this.endDate, 'month') ?? false)
        const isThisYear = this.date?.isSame(moment(), 'year') ?? false

        if (isSameMonthAndYear && isThisYear)
            // Jan 1 - 21
            return `${this.date?.format('MMM D')} - ${this.endDate?.format('D')}`
        else if (isSameYear && isThisYear)
            // Jan 1 - Feb 3
            return `${this.date?.format('MMM D')} - ${this.endDate?.format('MMM D')}`
        else if (isSameMonthAndYear)
            // Jan 1 - Jan 21, 2022
            return `${this.date?.format('MMM D')} - ${this.endDate?.format('D, YYYY')}`
        // Jan 1, 2021 - Jan 10, 2023
        else return `${this.date?.format('MMM D, YYYY')} - ${this.endDate?.format('MMM D, YYYY')}`
    }

    static dateRangeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'tw', label: 'This week' }))
        strings.push(new Option(undefined, { id: 'tm', label: 'This month' }))
        strings.push(new Option(undefined, { id: 'tq', label: 'This quarter' }))
        strings.push(new Option(undefined, { id: 'ty', label: 'This year' }))
        strings.push(new Option(undefined, { id: 'lw', label: 'Last week' }))
        strings.push(new Option(undefined, { id: 'lm', label: 'Last month' }))
        strings.push(new Option(undefined, { id: 'lq', label: 'Last quarter' }))
        strings.push(new Option(undefined, { id: 'ly', label: 'Last year' }))
        strings.push(new Option(undefined, { id: 'wtd', label: 'Week to date' }))
        strings.push(new Option(undefined, { id: 'mtd', label: 'Month to date' }))
        strings.push(new Option(undefined, { id: 'qtd', label: 'Quarter to date' }))
        strings.push(new Option(undefined, { id: 'ytd', label: 'Year to date' }))
        strings.push(new Option(undefined, { id: 'pw', label: 'Last 7 days' }))
        strings.push(new Option(undefined, { id: 'pm', label: 'Last 30 days' }))
        strings.push(new Option(undefined, { id: 'pq', label: 'Last 90 days' }))
        strings.push(new Option(undefined, { id: 'py', label: 'Last 365 days' }))
        strings.push(new Option(undefined, { id: 'nw', label: 'Next week' }))
        strings.push(new Option(undefined, { id: 'nm', label: 'Next month' }))
        strings.push(new Option(undefined, { id: 'nq', label: 'Next quarter' }))
        strings.push(new Option(undefined, { id: 'ny', label: 'Next year' }))
        strings.push(new Option(undefined, { id: 'all', label: 'All' }))
        strings.push(new Option(undefined, { id: 'custom', label: 'Custom' }))
        return strings
    }

    static allowReport(report: Report, user: User): boolean {
        return report.feature === undefined || Feature.enabled(report.feature.feature, user)
    }

    static allowCriteria(criteria: Criteria, user: User): boolean {
        return criteria.feature === undefined || Feature.enabled(criteria.feature.feature, user)
    }

    static allowComponent(component: PSComponent, user: User): boolean {
        return component.feature === undefined || Feature.enabled(component.feature.feature, user)
    }

    static timeComparisonStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'year', label: 'Year over year' }))
        strings.push(new Option(undefined, { id: 'period', label: 'Period over period' }))
        return strings
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.measures)
            this.measures = data.measures.map((measure: any) =>
                BaseRecord.fromSerializer(measure, PSComponent)
            )
        if (data.dimensions)
            this.dimensions = data.dimensions.map((dimension: any) =>
                BaseRecord.fromSerializer(dimension, PSComponent)
            )
        if (data.criterias)
            this.criterias = data.criterias.map((criteria: any) =>
                BaseRecord.fromSerializer(criteria, ReportCriteria)
            )
        if (data.date) this.date = moment(data.date)
        if (data.endDate) this.endDate = moment(data.endDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static dataType(): DataType {
        return 'reports'
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        components: ['measures', 'dimensions'],
        criterias: ['criterias'],
        columnExtractors: {
            components: 'component',
            criterias: 'criteria',
        },
    }

    jsonObject(): any {
        const jsonObject = super.jsonObject()
        if (!this.id && this.owner) jsonObject['owner'] = this.owner.id
        jsonObject['title'] = this.title ?? ''
        if (this.dateType === 'custom') jsonObject['date'] = this.date?.format('YYYY-MM-DD')
        if (this.dateType === 'custom') jsonObject['end_date'] = this.endDate?.format('YYYY-MM-DD')
        jsonObject['date_type'] = this.dateType ?? ''
        jsonObject['date_compare_type'] = this.dateCompareType ?? ''
        jsonObject['measures'] = this.measures.map((measure) => measure.id)
        jsonObject['dimensions'] = this.dimensions.map((dimension) => dimension.id)
        jsonObject['criterias'] = this.criterias.map((criteria) => criteria.jsonObject())
        return jsonObject
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get feature(): Feature | undefined {
        return this.getForeignRecord('feature', Feature)
    }
    set feature(feature: Feature | undefined) {
        this.setForeignRecord('feature', feature)
    }
}
